import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getMotorexDetail,
  schvalitMotorex,
  skrytMotorex,
  zrusitSchvaleniMotorex,
} from "../lib/api.js";
import type { MotorexProductDetail } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { PackageIcon, AlertIcon } from "../lib/icons.js";
import { FotkaVyrobce } from "./MotorexPage.js";

/**
 * MOTOREX detail produktu — co o něm ví výrobce a jestli to vedeme.
 *
 * 🔑 Od 15. 8. 2026 se odsud dá produkt pustit na eshop nebo ho schovat.
 *
 * ⚠ Schvalovačka tu jednou UŽ BYLA a zrušila se týž den — nedělala nic,
 * za tlačítkem nestála žádná pipeline. Teď stojí: `motorex_zapis.py` produkt
 * opravdu založí a robot `motorex_fronta.py` si frontu bere každých 15 minut.
 *
 * 💰 Cena: karta v ABŘE je → cenu drží ABRA (kánon platí dál); karta není →
 * pultovní ze stránky výrobce. ⛔⛔ NIKDY cena z feedu — je srovnávačová
 * a 13. 8. 2026 podle ní spadlo 31 živých cen dolů.
 */
export function MotorexDetailPage(): JSX.Element {
  const { itemId } = useParams<{ itemId: string }>();
  const [data, setData] = useState<MotorexProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState<string | null>(null);
  const [hlaska, setHlaska] = useState<{ typ: "ok" | "chyba"; text: string } | null>(null);

  const load = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    setError(null);
    getMotorexDetail(itemId)
      .then((d) => {
        setData(d);
        setActiveImage(d.imgUrl);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Společná obsluha tlačítek — po akci se detail vždy načte znovu ze serveru. */
  const akce = useCallback(
    async (jmeno: string, fn: () => Promise<void>, hotovo: string) => {
      setPracuje(jmeno);
      setHlaska(null);
      try {
        await fn();
        setHlaska({ typ: "ok", text: hotovo });
        load();
      } catch (e) {
        setHlaska({ typ: "chyba", text: popisChyby(e) });
      } finally {
        setPracuje(null);
      }
    },
    [load],
  );

  if (loading) return <LoadingBlock label="Načítám produkt…" />;
  if (error || !data) {
    return (
      <EmptyState
        icon={<PackageIcon size={28} />}
        title="Produkt nenalezen"
        description={error ?? `Položka ${itemId} v katalogu není.`}
      />
    );
  }

  const naEshopu = data.eshopStav === "na-eshopu";
  const ceka = data.eshopStav === "ceka";
  const skryto = data.eshopStav === "skryto";
  const images = [data.imgUrl, data.imgUrlAlt].filter((x): x is string => !!x);
  const skladem = data.abra.reduce((s, a) => s + a.qtyOnHand, 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-5">
        <Breadcrumbs
          items={[
            { label: "MOTOREX", to: "/motorex" },
            ...(data.categoryText
              ? [
                  {
                    label: data.categoryText,
                    to: `/motorex?category=${encodeURIComponent(data.categoryText)}`,
                  },
                ]
              : []),
            { label: data.name },
          ]}
        />
      </div>

      {data.scope === "undecided" && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertIcon size={18} className="text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-800">
            <strong>O téhle položce se ještě nerozhodlo.</strong> Přišla v
            „DOPORUČENÉ PRODUKTY", kde dodavatel míchá moto zboží s auty, nářadím
            a merchem. Zařaď ji natrvalo v{" "}
            <code className="text-xs bg-red-100 px-1 rounded">
              src/vendors/motorex/scope.ts
            </code>{" "}
            — jinak se bude ptát po každém importu.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Obrázky */}
        <div>
          <div className="aspect-square bg-zinc-50 rounded-2xl border border-zinc-200 overflow-hidden">
            <FotkaVyrobce
              src={activeImage}
              alt={data.name}
              ikona={48}
              className="w-full h-full object-contain p-6"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2">
              {images.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImage(src)}
                  className={`w-16 h-16 rounded-lg border overflow-hidden bg-white ${
                    activeImage === src ? "border-ktm-orange" : "border-zinc-200"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Údaje */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {data.stocked === true ? (
              <Badge tone="green">Vedeme</Badge>
            ) : data.stocked === false ? (
              <Badge tone="amber">Nevedeme</Badge>
            ) : (
              <Badge tone="neutral">Neznáme kód výrobce</Badge>
            )}
            {data.eshopStav === "na-eshopu" && <Badge tone="green">Na eshopu</Badge>}
            {data.eshopStav === "ceka" && <Badge tone="blue">Čeká na robota</Badge>}
            {data.eshopStav === "chyba" && <Badge tone="red">Zakládání selhalo</Badge>}
            {data.eshopStav === "skryto" && <Badge tone="neutral">Skryto z eshopu</Badge>}
            {data.status === "vanished" && <Badge tone="red">Zmizelo z feedu</Badge>}
            {data.scope === "allowlist" && <Badge tone="blue">Ručně schválený rozsah</Badge>}
          </div>

          <h1 className="mt-3 text-2xl font-semibold text-zinc-900">{data.name}</h1>

          <div className="mt-4 text-3xl font-semibold text-zinc-900">
            {data.priceRetail != null
              ? `${data.priceRetail.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč`
              : data.priceGross != null
                ? `${data.priceGross.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč`
                : "—"}
            <span className="ml-2 text-sm font-normal text-zinc-500">
              {data.priceRetail != null ? "pultovní u výrobce" : "srovnávačová z feedu"}
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            {data.priceRetail != null ? (
              <>
                Cena ze stránky výrobce. Feed hlásí{" "}
                {data.priceGross != null
                  ? `${data.priceGross.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč`
                  : "—"}{" "}
                — to je srovnávačová cena a bývá nižší.
              </>
            ) : (
              <>
                ⛔ Tohle je cena ze srovnávačového feedu, ne pultovní. U osmi ověřených kusů
                byla nižší u všech. Pultovní se doplní po přečtení stránky výrobce.
              </>
            )}
          </p>

          <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row
              label="Kód výrobce"
              value={
                data.manufacturerCode
                  ? data.manufacturerCodeSource === "ean"
                    ? `${data.manufacturerCode} (odhad z EAN)`
                    : data.manufacturerCode
                  : "—"
              }
              mono
            />
            <Row label="EAN" value={data.ean ?? "—"} mono />
            <Row label="ITEM_ID (jen PrestaShop)" value={data.itemId} mono />
            <Row label="Kategorie" value={data.categoryText ?? "—"} />
            <Row
              label="Kusů u výrobce"
              value={data.supplierStockQty != null ? String(data.supplierStockQty) : "—"}
            />
            <Row label="Naposled ve feedu" value={new Date(data.lastSeenAt).toLocaleDateString("cs-CZ")} />
          </dl>

          {/* Co o tom ví ABRA */}
          <div className="mt-6 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
            <div className="text-sm font-medium text-zinc-900">Naše karta v ABŘE</div>
            {data.abra.length === 0 ? (
              <p className="mt-1 text-xs text-zinc-500">
                {data.manufacturerCode
                  ? "Kartu s tímhle kódem výrobce v ABŘE nemáme — tenhle kus zatím neprodáváme."
                  : "Nemáme kód výrobce, takže se nedá zjistit, jestli to vedeme. Doplní se přečtením stránky výrobce."}
              </p>
            ) : (
              <>
                <p className="mt-1 text-xs text-zinc-500">
                  Sortiment i cenu určuje ABRA. Kód karty je zároveň SKU na eshopu.
                </p>
                <div className="mt-3 space-y-2">
                  {data.abra.map((a) => (
                    <div
                      key={a.abraCode}
                      className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className="font-mono text-xs text-zinc-500">{a.abraCode}</span>
                        <span className="tabular-nums">
                          {a.qtyOnHand > 0 ? (
                            <span className="font-medium text-emerald-700">
                              {a.qtyOnHand} ks skladem
                            </span>
                          ) : (
                            <span className="text-zinc-400">0 ks</span>
                          )}
                        </span>
                      </div>
                      <div className="mt-0.5 text-zinc-800">{a.abraName}</div>
                      {a.priceNoVat && (
                        <div className="mt-0.5 text-xs text-zinc-500">
                          naše cena {Number(a.priceNoVat).toLocaleString("cs-CZ")} Kč bez DPH
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {data.abra.length > 1 && (
                  <p className="mt-2 text-xs text-amber-700">
                    Karet je víc — bývá to dvojice kus × balení. Na eshop patří ta,
                    kterou zákazník kupuje.
                  </p>
                )}
                {skladem === 0 && (
                  <p className="mt-2 text-xs text-zinc-500">
                    Nulová zásoba neznamená, že to neprodáváme — jen to zrovna došlo.
                  </p>
                )}
              </>
            )}
          </div>

          {/* ── Ovládání eshopu ────────────────────────────────────────── */}
          <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-sm font-medium text-zinc-900">Eshop</div>

            {/*
              💰 Karta v ABŘE je → cenu drží ABRA (kánon platí dál).
              Karta není → pultovní ze stránky výrobce. ⛔⛔ NIKDY cena z feedu.
            */}
            {!naEshopu && (
              <p className="mt-2 text-xs text-zinc-600">
                {data.abra.length > 0 ? (
                  <>
                    Cena se vezme <strong>z ABRY</strong> — kartu na to máme.
                  </>
                ) : data.priceRetail != null ? (
                  <>
                    Kartu v ABŘE nemáme, takže cena bude{" "}
                    <strong>pultovní ze stránky výrobce</strong> (
                    {data.priceRetail.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč).
                    Do vzniku karty u produktu <strong>nesedí sklad</strong>.
                  </>
                ) : (
                  <span className="text-amber-700">
                    ⚠ Nemá kartu v ABŘE ani pultovní cenu — schválit to nejde. Cena z feedu je
                    srovnávačová a použít se nesmí.
                  </span>
                )}
              </p>
            )}

            {ceka && (
              <p className="mt-2 text-xs text-sky-700">
                Schváleno {kdy(data.approvedAt)}
                {data.approvedBy ? ` (${data.approvedBy})` : ""} — robot zakládá každých 15 minut.
              </p>
            )}
            {naEshopu && data.foundedAt && (
              <p className="mt-2 text-xs text-emerald-700">Založeno {kdy(data.foundedAt)}.</p>
            )}
            {skryto && (
              <p className="mt-2 text-xs text-zinc-500">
                Skryto {kdy(data.hiddenAt)} — produkt v adminu zůstal, jen ho zákazník nevidí.
              </p>
            )}
            {data.foundedError && (
              <p className="mt-2 text-xs text-red-700">
                Robot to zkusil a nepovedlo se:{" "}
                <code className="bg-red-50 px-1 rounded">{data.foundedError}</code> Zůstává ve
                frontě a zkusí to znovu.
              </p>
            )}

            <div className="mt-3 flex gap-2 flex-wrap">
              {!naEshopu && !ceka && !skryto && (
                <Button
                  onClick={() =>
                    akce(
                      "schvalit",
                      () => schvalitMotorex(data.itemId),
                      "Schváleno — robot to založí do 15 minut.",
                    )
                  }
                  disabled={
                    pracuje !== null || (data.abra.length === 0 && data.priceRetail == null)
                  }
                >
                  {pracuje === "schvalit" ? "Schvaluji…" : "Schválit na eshop"}
                </Button>
              )}
              {ceka && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    akce("zrusit", () => zrusitSchvaleniMotorex(data.itemId), "Schválení zrušeno.")
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "zrusit" ? "Ruším…" : "Zrušit schválení"}
                </Button>
              )}
              {(naEshopu || data.shopifyProductId !== null) && !skryto && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    akce(
                      "skryt",
                      () => skrytMotorex(data.itemId),
                      "Skryto — zákazník už produkt nevidí.",
                    )
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "skryt" ? "Skrývám…" : "Skrýt z eshopu"}
                </Button>
              )}
              {skryto && (
                <Button
                  onClick={() =>
                    akce(
                      "schvalit",
                      () => schvalitMotorex(data.itemId),
                      "Schváleno — robot produkt vrátí do 15 minut.",
                    )
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "schvalit" ? "Vracím…" : "Vrátit na eshop"}
                </Button>
              )}
            </div>

            {hlaska && (
              <p
                className={`mt-3 text-sm ${
                  hlaska.typ === "ok" ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {hlaska.text}
              </p>
            )}
          </div>

          {data.externalUrl && (
            <a
              href={data.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block text-sm text-ktm-orange hover:underline"
            >
              Otevřít u dodavatele →
            </a>
          )}
        </div>
      </div>

      {data.descriptionHtml && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-zinc-900">Popis od výrobce</h2>
          <div
            className="mt-3 prose prose-sm max-w-none text-zinc-700"
            // Popis je HTML z feedu dodavatele — stejně jako u MEFO a Garminu.
            dangerouslySetInnerHTML={{ __html: data.descriptionHtml }}
          />
        </section>
      )}

      {data.accessories.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-zinc-900">
            Související zboží{" "}
            <span className="text-sm font-normal text-zinc-400">
              ({data.accessories.length} z toho, co máme)
            </span>
          </h2>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {data.accessories.map((a) => (
              <Link
                key={a.itemId}
                to={`/motorex/${encodeURIComponent(a.itemId)}`}
                className="rounded-xl border border-zinc-200 bg-white p-2 hover:border-ktm-orange transition-colors"
              >
                <div className="aspect-square bg-zinc-50 rounded-lg overflow-hidden">
                  {a.imgUrl ? (
                    <img src={a.imgUrl} alt={a.name} loading="lazy" className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-300">
                      <PackageIcon size={24} />
                    </div>
                  )}
                </div>
                <div className="mt-1.5 text-xs text-zinc-700 line-clamp-2">{a.name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className={`mt-0.5 text-zinc-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}

/** Chyby ze serveru mají strojové kódy — tady se z nich dělá česká věta. */
function popisChyby(e: unknown): string {
  const kod = e instanceof Error ? e.message : String(e);
  const slovnik: Record<string, string> = {
    uz_je_na_eshopu: "Produkt už na eshopu je.",
    na_eshopu_neni: "Na eshopu pod tímhle SKU nic není — není co skrývat.",
    bez_kodu_vyrobce: "Neznáme kód výrobce — doplní se přečtením stránky výrobce.",
    bez_ceny: "Nemá cenu v ABŘE ani pultovní. Cena z feedu je srovnávačová, použít ji nelze.",
    robot_secret_not_configured:
      "Server nemá nastavené ROBOT_SECRET, takže by si robot frontu nevzal.",
    not_found: "Položka v katalogu není.",
    shopify: "Shopify zápis odmítlo — podrobnosti jsou v logu serveru.",
  };
  return slovnik[kod] ?? `Nepovedlo se: ${kod}`;
}

function kdy(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("cs-CZ") : "—";
}
